// Unpublished Work © 2025-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {IconDelete} from '@deere/icons';
import SearchFilter from 'Ui/components/higher-order-components/search-filter';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import OnLinkIconButton from 'Ui/components/common/onlink-icon-button';
import Search from 'Ui/components/search/search';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {connect} from 'react-redux';
import {DataGrid, Box} from '@deere/isg.component-library';

const ICON_STYLE = {
    style: {
        height: '20px',
        width: '20px'
    }
};

const DATAGRID_ROW_HEIGHT = 52;
const DATAGRID_HEADER_HEIGHT = 56;
const DATAGRID_CHROME_HEIGHT = 2;
const DEFAULT_MOBILE_MAX_HEIGHT_OFFSET = 250;

export const DEFAULT_DESKTOP_MAX_HEIGHT_OFFSET = 224;

function getContentHeight(rowCount, hasAggregation, maxVisibleRows, pinnedRows) {
    const aggregationRows = hasAggregation ? 1 : 0;
    const visibleRowCount = maxVisibleRows ? Math.min(rowCount, maxVisibleRows) : rowCount;

    return DATAGRID_HEADER_HEIGHT + (visibleRowCount + aggregationRows + pinnedRows) * DATAGRID_ROW_HEIGHT + DATAGRID_CHROME_HEIGHT;
}

function getColumns(columns, showDeleteColumn, deleteFilter, deleteHandler, myJdPermissions, isMigrated) {
    const filteredColumns = columns.filter((item) => isAuthorized({
        myJdPermissions: item.requiredMyJdPermissions
    }, {
        myJdPermissions,
        isMigrated
    }));

    if (showDeleteColumn) {
        return [
            ...filteredColumns,
            {
                field: 'delete',
                headerName: '',
                width: 50,
                sortable: false,
                renderCell: (params) => {
                    const rowData = params.row;
                    const rowIndex = params.row.index;
                    const showDeleteIcon = deleteFilter ? deleteFilter(rowData, rowIndex) : true;

                    return showDeleteIcon &&
                        (

                            <Box
                                sx={{
                                    display: 'flex',
                                    marginRight: '8px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    height: '100%'
                                }}
                            >
                                <OnLinkIconButton
                                    onClick={() => deleteHandler(rowData, rowIndex)}
                                >
                                    <IconDelete {...ICON_STYLE}/>
                                </OnLinkIconButton></Box>);
                }
            }
        ];
    }

    return filteredColumns;
}

function getTable(tableProps, translations) {
    if (tableProps.data.length === 0) {
        return (
            <h2 className='table-no-data'>
                {tableProps.noDataAvailableMessage || translations.NO_LOADED_DATA_AVAILABLE}
            </h2>
        );
    }

    return (
        <DataGrid
            columns={tableProps.columns}
            getRowId={tableProps.getRowId}
            initialState={tableProps.initialState}
            rows={tableProps.data}
            slotProps={tableProps.slotProps}
            sx={{
                height: '100%'
            }}
            {...tableProps}
        />
    );
}

function renderGridWithSideComponent(sideComponent, tableProps, translations) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                height: '100%',
                flexDirection: {
                    xs: 'column',
                    sm: 'row'
                }
            }}
        >
            {sideComponent}
            <Box
                sx={{
                    height: '590px',
                    flexGrow: 1,
                    overflow: 'hidden',
                    width: {
                        xs: '100%',
                        sm: 'auto'
                    }
                }}
            >
                {getTable(tableProps, translations)}
            </Box>
        </Box>
    );
}

function DataGridWrapper(props) {
    const {
        additionalTableOptionRow,
        applySearch,
        columns = [],
        deleteFilter,
        deleteHandler,
        filterComponent = null,
        headerComponent,
        legendComponent,
        loading = false,
        noDataAvailableMessage,
        getRowId,
        onSearchChange,
        pivotBy = [],
        rows = [],
        search,
        searchable,
        searchFields,
        showDeleteColumn,
        sideComponent,
        translations,
        myJdPermissions,
        initialState,
        slotProps,
        isMigrated,
        maxHeightOffset = DEFAULT_DESKTOP_MAX_HEIGHT_OFFSET,
        maxVisibleRows,
        ...otherProps
    } = props;

    const tableProps = {
        columns: getColumns(columns, showDeleteColumn, deleteFilter, deleteHandler, myJdPermissions, isMigrated),
        data: applySearch(rows, searchFields),
        noDataAvailableMessage,
        pivotBy,
        initialState,
        slotProps,
        getRowId,
        ...otherProps
    };

    tableProps.pageSize = tableProps.data.length;

    const pinnedRowsCount = (slotProps?.root?.pinnedRows?.top?.length ?? 0) + (slotProps?.root?.pinnedRows?.bottom?.length ?? 0);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                flex: '1 1 auto',
                minHeight: 0,
                overflow: 'hidden',
                maxHeight: {
                    xs: `calc(100dvh - ${DEFAULT_MOBILE_MAX_HEIGHT_OFFSET}px)`,
                    md: maxHeightOffset ? `calc(100dvh - ${maxHeightOffset}px)` : '100%'
                }
            }}
        >
            {
                headerComponent &&
                <div
                    className='table-header-container'
                    data-testid={'mock-table-header-container'}
                >
                    {headerComponent}
                </div>
            }
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: {
                        xs: 'column',
                        sm: 'row'
                    }
                }}
            >
                {
                    searchable &&
                    <Search
                        debounce={500}
                        handler={onSearchChange}
                        search={search}
                        translations={translations}
                    />
                }
                {filterComponent}
            </Box>
            {additionalTableOptionRow}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    minHeight: 0,
                    maxHeight: `${getContentHeight(tableProps.data.length, Boolean(otherProps.aggregation), maxVisibleRows, pinnedRowsCount)}px`
                }}
            >
                <LoadingWrapper
                    alwaysRenderChildren={false}
                    className='table-loading-icon'
                    loading={loading}
                    size='50px'
                >
                    {
                        sideComponent ? renderGridWithSideComponent(sideComponent, tableProps, translations) : getTable(tableProps, translations)
                    }
                </LoadingWrapper>
            </Box>
            {!loading && legendComponent}
        </Box>
    );
}

DataGridWrapper.propTypes = {
    additionalTableOptionRow: PropTypes.element,
    applySearch: PropTypes.func,
    className: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.object),
    deleteFilter: PropTypes.func,
    deleteHandler: PropTypes.func,
    filterComponent: PropTypes.element,
    getRowId: PropTypes.func,
    headerComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    initialState: PropTypes.object,
    isMigrated: PropTypes.bool,
    legendComponent: PropTypes.element,
    loading: PropTypes.bool,
    maxHeightOffset: PropTypes.number,
    maxVisibleRows: PropTypes.number,
    myJdPermissions: PropTypes.myJdPermissions,
    noDataAvailableMessage: PropTypes.string,
    onSearchChange: PropTypes.func,
    paginationThreshold: PropTypes.number,
    pivotBy: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.object),
    search: PropTypes.string,
    searchable: PropTypes.bool,
    searchFields: PropTypes.arrayOf(PropTypes.string),
    showDeleteColumn: PropTypes.bool,
    sideComponent: PropTypes.element,
    slotProps: PropTypes.object,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions
    };
}

export default connect(mapStateToProps)(SearchFilter(DataGridWrapper));
